import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingBag } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import api from '@/utils/api';
import { useCart } from '@/context/CartContext';
import { formatGs, productImg } from '@/utils/helpers';
import { toast } from 'sonner';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, branchId } = useCart();
  const [product, setProduct] = useState(null);
  const [branchPrice, setBranchPrice] = useState(0);
  const [promoInfo, setPromoInfo] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [branchVariants, setBranchVariants] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/productos/${id}`).then(r => {
      setProduct(r.data.producto);
      setLoading(false);
    }).catch(() => setLoading(false));

    if (branchId) {
      api.get(`/productos?branch_id=${branchId}`).then(r => {
        const found = r.data.productos.find(p => p.id === parseInt(id));
        if (found) {
          const current = found.current_price ? parseFloat(found.current_price) : (found.branch_price ? parseFloat(found.branch_price) : 0);
          setBranchPrice(current);
          setPromoInfo(found.promo_active ? { regular: parseFloat(found.branch_price), label: found.promo_label } : null);
        }
      }).catch(() => {});
      // Fetch branch-specific variant prices
      api.get(`/productos/${id}/variantes/sucursal/${branchId}`).then(r => {
        setBranchVariants(r.data.variantes || []);
      }).catch(() => {});
    }
  }, [id, branchId]);

  const toggleAddon = (addon) => {
    setSelectedAddons(prev =>
      prev.find(a => a.id === addon.id) ? prev.filter(a => a.id !== addon.id) : [...prev, addon]
    );
  };

  const addonsCost = selectedAddons.reduce((s, a) => s + parseFloat(a.extra_cost), 0);
  // Resolve variant price: use branch-specific variant price if available
  const getVariantPrice = (v) => {
    if (branchVariants) {
      const bv = branchVariants.find(bv => bv.id === v.id);
      if (bv) return parseFloat(bv.effective_price);
    }
    return parseFloat(v.price_default || v.price);
  };
  const effectivePrice = selectedVariant ? getVariantPrice(selectedVariant) : branchPrice;
  const itemTotal = (effectivePrice + addonsCost) * quantity;

  const activeVariants = (product?.variants || []).filter(v => v.active);
  const needsVariant = activeVariants.length > 0;
  const canAdd = !needsVariant || selectedVariant;

  const handleAdd = () => {
    if (!product || !canAdd) return;
    const { wasUpdate, quantity: qty } = addItem(product, quantity, selectedAddons, effectivePrice, selectedVariant);
    toast(wasUpdate ? `Cantidad actualizada: x${qty}` : `Agregado al carrito: ${product.name}`, {
      description: wasUpdate ? product.name : selectedVariant ? `${selectedVariant.name} x${quantity}` : `x${quantity}`,
      duration: 2500,
      action: { label: 'Ver carrito', onClick: () => navigate('/carrito') },
    });
    navigate(-1);
  };

  if (loading) return (
    <div className="pb-nav">
      <div className="h-64 bg-zinc-900 animate-pulse" />
      <div className="p-4 space-y-4">
        <div className="h-8 bg-zinc-800 rounded w-3/4 animate-pulse" />
        <div className="h-4 bg-zinc-800 rounded w-full animate-pulse" />
      </div>
    </div>
  );

  if (!product) return <div className="p-8 text-center text-zinc-500">Producto no encontrado.</div>;

  const activeAddons = (product.addons || []).filter(a => a.active);

  return (
    <div data-testid="product-detail-page" className="pb-nav">
      {/* Image */}
      <div className="relative">
        {productImg(product.image_url) ? (
          <img src={productImg(product.image_url)} alt={product.name} className="w-full h-64 md:h-80 object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-64 bg-zinc-800 flex items-center justify-center">
            <span className="text-zinc-600 text-6xl font-bold">{product.name[0]}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        <button data-testid="product-back-btn" onClick={() => navigate(-1)} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white">
          <ArrowLeft size={20} />
        </button>
      </div>

      <div className="px-4 -mt-8 relative z-10">
        <span className="text-xs text-[#E84C1C] font-medium uppercase tracking-wider">{product.category_name}</span>
        <h2 className="text-2xl font-bold mt-1">{product.name}</h2>
        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{product.description}</p>
        <div className="mt-3" data-testid="product-price">
          {activeVariants.length > 0 ? (
            <p className="text-zinc-400 text-sm">Seleccioná una opción abajo</p>
          ) : branchPrice > 0 ? (
            promoInfo ? (
              <div>
                <span className="text-zinc-500 text-lg line-through mr-2">{formatGs(promoInfo.regular)}</span>
                <span className="text-[#E84C1C] text-2xl font-bold">{formatGs(branchPrice)}</span>
                <p className="text-[#E84C1C]/80 text-xs mt-1">{promoInfo.label}</p>
              </div>
            ) : (
              <p className="text-[#E84C1C] text-2xl font-bold">{formatGs(branchPrice)}</p>
            )
          ) : (
            <p className="text-zinc-500 text-sm">Seleccioná tu zona para ver el precio</p>
          )}
        </div>
      </div>

      {/* Variants */}
      {activeVariants.length > 0 && (
        <div className="mt-6 px-4">
          <h3 className="text-sm font-semibold text-zinc-300 mb-1">
            Elegí una opción {needsVariant && <span className="text-[#E84C1C]">*</span>}
          </h3>
          {needsVariant && !selectedVariant && (
            <p className="text-xs text-amber-400 mb-2">Seleccioná una variante para continuar</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            {activeVariants.map(v => (
              <button
                key={v.id}
                data-testid={`variant-${v.id}`}
                onClick={() => setSelectedVariant(v)}
                className={`p-3 rounded-xl border text-left transition-all ${selectedVariant?.id === v.id ? 'bg-[#E84C1C]/10 border-[#E84C1C]/40 ring-1 ring-[#E84C1C]/30' : 'bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700'}`}
              >
                <p className="text-sm font-medium">{v.name}</p>
                <p className={`text-base font-bold mt-0.5 ${selectedVariant?.id === v.id ? 'text-[#E84C1C]' : 'text-zinc-300'}`}>
                  {formatGs(getVariantPrice(v))}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Addons */}
      {activeAddons.length > 0 && (
        <div className="mt-6 px-4">
          <h3 className="text-sm font-semibold text-zinc-300 mb-3">Complementos</h3>
          <div className="space-y-2">
            {activeAddons.map(addon => (
              <label
                key={addon.id}
                data-testid={`addon-${addon.id}`}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedAddons.find(a => a.id === addon.id) ? 'bg-[#E84C1C]/10 border-[#E84C1C]/30' : 'bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700'}`}
              >
                <Checkbox
                  checked={!!selectedAddons.find(a => a.id === addon.id)}
                  onCheckedChange={() => toggleAddon(addon)}
                  className="border-zinc-600 data-[state=checked]:bg-[#E84C1C] data-[state=checked]:border-[#E84C1C]"
                />
                <span className="flex-1 text-sm">{addon.name}</span>
                <span className="text-xs text-[#E84C1C] font-medium">+{formatGs(addon.extra_cost)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Quantity + Add */}
      <div className="fixed bottom-16 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-md border-t border-zinc-800/50 p-4 z-30">
        <div className="flex items-center gap-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3 bg-zinc-800 rounded-full px-1 py-1">
            <button data-testid="qty-minus" onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center hover:bg-zinc-600 transition-colors">
              <Minus size={16} />
            </button>
            <span className="text-base font-bold w-6 text-center" data-testid="qty-value">{quantity}</span>
            <button data-testid="qty-plus" onClick={() => setQuantity(q => q + 1)} className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center hover:bg-zinc-600 transition-colors">
              <Plus size={16} />
            </button>
          </div>
          <button
            data-testid="product-add-to-cart"
            onClick={handleAdd}
            disabled={!canAdd}
            className={`flex-1 font-medium py-3 rounded-full active:scale-[0.98] transition-all flex items-center justify-center gap-2 ${canAdd ? 'bg-[#E84C1C] text-white hover:bg-[#d43f12] shadow-[0_0_20px_rgba(232,76,28,0.3)]' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
          >
            <ShoppingBag size={18} />
            {!canAdd ? 'Elegí una opción' : `Agregar ${effectivePrice > 0 ? formatGs(itemTotal) : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
